import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Plus, Trash2, GripVertical, Save, ChevronDown, ChevronUp, X, ImagePlus, Loader2, ArrowLeftRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

export default function ModuleEditorPage() {
  const { moduleId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedLesson, setExpandedLesson] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null);

  const loadModule = useCallback(async () => {
    try {
      const { data } = await api('get', `/training-modules/${moduleId}`);
      setModule(data);
      if (data.lessons?.length > 0) setExpandedLesson(0);
    } catch {
      toast.error('Failed to load module');
      navigate('/admin');
    }
  }, [api, moduleId, navigate]);

  useEffect(() => { loadModule(); }, [loadModule]);

  const updateField = (field, value) => {
    setModule(prev => ({ ...prev, [field]: value }));
  };

  const updateLesson = (index, field, value) => {
    setModule(prev => {
      const lessons = [...(prev.lessons || [])];
      lessons[index] = { ...lessons[index], [field]: value };
      return { ...prev, lessons };
    });
  };

  const updateKeyPoint = (lessonIdx, pointIdx, value) => {
    setModule(prev => {
      const lessons = [...(prev.lessons || [])];
      const points = [...(lessons[lessonIdx].key_points || [])];
      points[pointIdx] = value;
      lessons[lessonIdx] = { ...lessons[lessonIdx], key_points: points };
      return { ...prev, lessons };
    });
  };

  const addKeyPoint = (lessonIdx) => {
    setModule(prev => {
      const lessons = [...(prev.lessons || [])];
      const points = [...(lessons[lessonIdx].key_points || []), ''];
      lessons[lessonIdx] = { ...lessons[lessonIdx], key_points: points };
      return { ...prev, lessons };
    });
  };

  const removeKeyPoint = (lessonIdx, pointIdx) => {
    setModule(prev => {
      const lessons = [...(prev.lessons || [])];
      const points = (lessons[lessonIdx].key_points || []).filter((_, i) => i !== pointIdx);
      lessons[lessonIdx] = { ...lessons[lessonIdx], key_points: points };
      return { ...prev, lessons };
    });
  };

  const uploadImage = async (lessonIdx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(lessonIdx);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api('post', '/images/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const imgUrl = `${process.env.REACT_APP_BACKEND_URL}/api/images/${data.path}`;
      setModule(prev => {
        const lessons = [...(prev.lessons || [])];
        const images = [...(lessons[lessonIdx].images || []), { url: imgUrl, caption: '' }];
        lessons[lessonIdx] = { ...lessons[lessonIdx], images };
        return { ...prev, lessons };
      });
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(null);
      e.target.value = '';
    }
  };

  const removeImage = (lessonIdx, imgIdx) => {
    setModule(prev => {
      const lessons = [...(prev.lessons || [])];
      const images = (lessons[lessonIdx].images || []).filter((_, i) => i !== imgIdx);
      lessons[lessonIdx] = { ...lessons[lessonIdx], images };
      return { ...prev, lessons };
    });
  };

  const updateImageCaption = (lessonIdx, imgIdx, caption) => {
    setModule(prev => {
      const lessons = [...(prev.lessons || [])];
      const images = [...(lessons[lessonIdx].images || [])];
      const img = images[imgIdx];
      images[imgIdx] = typeof img === 'string' ? { url: img, caption } : { ...img, caption };
      lessons[lessonIdx] = { ...lessons[lessonIdx], images };
      return { ...prev, lessons };
    });
  };

  const moveImage = (lessonIdx, imgIdx, direction) => {
    const newIdx = imgIdx + direction;
    setModule(prev => {
      const lessons = [...(prev.lessons || [])];
      const images = [...(lessons[lessonIdx].images || [])];
      if (newIdx < 0 || newIdx >= images.length) return prev;
      [images[imgIdx], images[newIdx]] = [images[newIdx], images[imgIdx]];
      lessons[lessonIdx] = { ...lessons[lessonIdx], images };
      return { ...prev, lessons };
    });
  };

  const addLesson = () => {
    setModule(prev => {
      const lessons = [...(prev.lessons || []), { title: 'New Lesson', content: '', key_points: [] }];
      return { ...prev, lessons };
    });
    setExpandedLesson((module?.lessons?.length) || 0);
  };

  const removeLesson = (index) => {
    if (!window.confirm(`Delete "${module.lessons[index]?.title}"?`)) return;
    setModule(prev => {
      const lessons = (prev.lessons || []).filter((_, i) => i !== index);
      return { ...prev, lessons };
    });
    setExpandedLesson(null);
  };

  const moveLesson = (index, direction) => {
    const newIdx = index + direction;
    if (newIdx < 0 || newIdx >= (module?.lessons?.length || 0)) return;
    setModule(prev => {
      const lessons = [...(prev.lessons || [])];
      [lessons[index], lessons[newIdx]] = [lessons[newIdx], lessons[index]];
      return { ...prev, lessons };
    });
    setExpandedLesson(newIdx);
  };

  const saveModule = async () => {
    if (!module) return;
    setSaving(true);
    try {
      await api('put', `/training-modules/${moduleId}`, {
        title: module.title,
        description: module.description,
        lessons: module.lessons,
        practical_checklist: module.practical_checklist || []
      });
      toast.success('Module saved successfully');
    } catch {
      toast.error('Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  if (!module) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin w-8 h-8 border-2 border-[#1B2A4A] border-t-transparent rounded-full" />
      </div>
    );
  }

  const lessons = module.lessons || [];

  return (
    <div className="pb-20 md:pb-6 max-w-4xl mx-auto">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Button variant="ghost" onClick={() => navigate('/admin')} className="text-stone-600 hover:text-stone-900 -ml-2 rounded-xl" data-testid="back-to-admin">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Admin
          </Button>
          <Button onClick={saveModule} disabled={saving} className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="save-all-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-stone-900 mt-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Edit Module
        </h1>
      </motion.div>

      {/* Module Title & Description */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm mb-4" data-testid="module-header-editor">
          <CardContent className="p-5 space-y-4">
            <div>
              <Label className="text-stone-700 font-medium text-sm">Module Title</Label>
              <Input value={module.title} onChange={e => updateField('title', e.target.value)}
                className="rounded-xl border-stone-200 mt-1 text-lg font-semibold" data-testid="module-title-input"
                style={{ fontFamily: "'Outfit', sans-serif" }} />
            </div>
            <div>
              <Label className="text-stone-700 font-medium text-sm">Description</Label>
              <Textarea value={module.description} onChange={e => updateField('description', e.target.value)}
                className="rounded-xl border-stone-200 mt-1" rows={2} data-testid="module-desc-input" />
            </div>
            <div className="flex items-center gap-2 text-xs text-stone-400">
              <Badge className="bg-stone-100 text-stone-600">{lessons.length} lesson{lessons.length !== 1 ? 's' : ''}</Badge>
              {module.document_name && <span>Source: {module.document_name}</span>}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Lessons List */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Lessons</h2>
        <Button onClick={addLesson} variant="outline" className="rounded-xl text-sm border-stone-200" data-testid="add-lesson-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Lesson
        </Button>
      </div>

      <div className="space-y-2" data-testid="lessons-editor-list">
        <AnimatePresence>
          {lessons.map((lesson, idx) => {
            const isExpanded = expandedLesson === idx;
            return (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ delay: idx * 0.03 }}
              >
                <Card className={`rounded-2xl border shadow-sm transition-all ${isExpanded ? 'border-[#1B2A4A]/30 bg-white' : 'border-stone-200 bg-white'}`}
                  data-testid={`lesson-editor-${idx}`}>
                  <CardContent className="p-0">
                    {/* Lesson Header (always visible) */}
                    <div
                      className="flex items-center gap-2 p-4 cursor-pointer hover:bg-stone-50 rounded-t-2xl transition-colors"
                      onClick={() => setExpandedLesson(isExpanded ? null : idx)}
                      data-testid={`lesson-toggle-${idx}`}
                    >
                      <GripVertical className="w-4 h-4 text-stone-300 shrink-0" />
                      <Badge className="bg-[#1B2A4A]/10 text-[#1B2A4A] text-xs shrink-0">{idx + 1}</Badge>
                      <span className="flex-1 text-sm font-medium text-stone-900 truncate" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {lesson.title || 'Untitled Lesson'}
                      </span>
                      <div className="flex items-center gap-1 shrink-0">
                        {idx > 0 && (
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); moveLesson(idx, -1); }}
                            className="p-1 h-7 w-7 text-stone-400 hover:text-stone-600" data-testid={`move-up-${idx}`}>
                            <ChevronUp className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {idx < lessons.length - 1 && (
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); moveLesson(idx, 1); }}
                            className="p-1 h-7 w-7 text-stone-400 hover:text-stone-600" data-testid={`move-down-${idx}`}>
                            <ChevronDown className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); removeLesson(idx); }}
                          className="p-1 h-7 w-7 text-red-400 hover:text-red-600" data-testid={`delete-lesson-${idx}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
                      </div>
                    </div>

                    {/* Expanded Editor */}
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-stone-100"
                      >
                        <div className="p-4 space-y-4">
                          {/* Lesson Title */}
                          <div>
                            <Label className="text-stone-700 font-medium text-xs">Lesson Title</Label>
                            <Input value={lesson.title} onChange={e => updateLesson(idx, 'title', e.target.value)}
                              className="rounded-xl border-stone-200 mt-1" data-testid={`lesson-title-input-${idx}`} />
                          </div>

                          {/* Lesson Content */}
                          <div>
                            <Label className="text-stone-700 font-medium text-xs">Content</Label>
                            <Textarea value={lesson.content} onChange={e => updateLesson(idx, 'content', e.target.value)}
                              className="rounded-xl border-stone-200 mt-1 text-sm leading-relaxed" rows={6}
                              data-testid={`lesson-content-input-${idx}`} />
                            <p className="text-[10px] text-stone-400 mt-1">{(lesson.content || '').length} characters</p>
                          </div>

                          {/* Key Points */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-stone-700 font-medium text-xs">Key Takeaways</Label>
                              <Button variant="ghost" size="sm" onClick={() => addKeyPoint(idx)}
                                className="text-xs text-[#1B2A4A] h-7 px-2" data-testid={`add-keypoint-${idx}`}>
                                <Plus className="w-3 h-3 mr-0.5" /> Add
                              </Button>
                            </div>
                            <div className="space-y-2">
                              {(lesson.key_points || []).map((point, pIdx) => (
                                <div key={pIdx} className="flex items-start gap-2">
                                  <div className="w-5 h-5 rounded-full bg-[#D9480F] text-white text-[10px] flex items-center justify-center shrink-0 mt-2">
                                    {pIdx + 1}
                                  </div>
                                  <Input value={point} onChange={e => updateKeyPoint(idx, pIdx, e.target.value)}
                                    className="rounded-lg border-stone-200 text-sm flex-1" placeholder="Key takeaway..."
                                    data-testid={`keypoint-input-${idx}-${pIdx}`} />
                                  <Button variant="ghost" size="sm" onClick={() => removeKeyPoint(idx, pIdx)}
                                    className="p-1 h-8 w-8 text-stone-400 hover:text-red-500 shrink-0" data-testid={`remove-keypoint-${idx}-${pIdx}`}>
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              ))}
                              {(!lesson.key_points || lesson.key_points.length === 0) && (
                                <p className="text-xs text-stone-400 italic">No key points yet. Click "Add" to create one.</p>
                              )}
                            </div>
                          </div>

                          {/* Lesson Images */}
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <Label className="text-stone-700 font-medium text-xs">Images</Label>
                              <label className="cursor-pointer">
                                <input type="file" accept="image/*" onChange={(e) => uploadImage(idx, e)} className="hidden"
                                  disabled={uploadingImage === idx} data-testid={`upload-image-${idx}`} />
                                <Button variant="ghost" size="sm" asChild className="text-xs text-[#1B2A4A] h-7 px-2" disabled={uploadingImage === idx}>
                                  <span>
                                    {uploadingImage === idx ? <Loader2 className="w-3 h-3 mr-0.5 animate-spin" /> : <ImagePlus className="w-3 h-3 mr-0.5" />}
                                    {uploadingImage === idx ? 'Uploading...' : 'Add Image'}
                                  </span>
                                </Button>
                              </label>
                            </div>
                            {(lesson.images || []).length > 0 && (
                              <div className="space-y-2">
                                {(lesson.images || []).map((img, imgIdx) => {
                                  const imgUrl = typeof img === 'string' ? img : img.url;
                                  const caption = typeof img === 'string' ? '' : (img.caption || '');
                                  return (
                                    <div key={imgUrl} className="flex gap-2 items-start p-2 rounded-xl border border-stone-100 bg-stone-50" data-testid={`image-item-${idx}-${imgIdx}`}>
                                      <img src={imgUrl} alt={caption || `Image ${imgIdx + 1}`} className="w-20 h-16 object-cover rounded-lg shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <Input value={caption} onChange={e => updateImageCaption(idx, imgIdx, e.target.value)}
                                          className="rounded-lg border-stone-200 text-xs h-8 mb-1" placeholder="Add a caption..."
                                          data-testid={`image-caption-${idx}-${imgIdx}`} />
                                        <div className="flex items-center gap-1">
                                          {imgIdx > 0 && (
                                            <Button variant="ghost" size="sm" onClick={() => moveImage(idx, imgIdx, -1)}
                                              className="p-0 h-6 w-6 text-stone-400 hover:text-[#1B2A4A]" data-testid={`move-image-left-${idx}-${imgIdx}`}>
                                              <ChevronUp className="w-3 h-3" />
                                            </Button>
                                          )}
                                          {imgIdx < (lesson.images || []).length - 1 && (
                                            <Button variant="ghost" size="sm" onClick={() => moveImage(idx, imgIdx, 1)}
                                              className="p-0 h-6 w-6 text-stone-400 hover:text-[#1B2A4A]" data-testid={`move-image-right-${idx}-${imgIdx}`}>
                                              <ChevronDown className="w-3 h-3" />
                                            </Button>
                                          )}
                                          <Button variant="ghost" size="sm" onClick={() => removeImage(idx, imgIdx)}
                                            className="p-0 h-6 w-6 text-red-400 hover:text-red-600 ml-auto" data-testid={`remove-image-${idx}-${imgIdx}`}>
                                            <X className="w-3 h-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {(!lesson.images || lesson.images.length === 0) && (
                              <p className="text-xs text-stone-400 italic">No images. Upload images to support this lesson.</p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {lessons.length === 0 && (
          <Card className="bg-white rounded-2xl border border-stone-200">
            <CardContent className="p-8 text-center">
              <p className="text-stone-500 mb-3">No lessons yet</p>
              <Button onClick={addLesson} className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="add-first-lesson-btn">
                <Plus className="w-4 h-4 mr-1" /> Add First Lesson
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Practical checklist editor */}
      <Card className="bg-white rounded-2xl border border-stone-200 shadow-sm mt-6" data-testid="practical-checklist-editor">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-semibold text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>Practical Sign-off Checklist</h3>
            <Button variant="outline" size="sm" onClick={() => setModule(prev => ({ ...prev, practical_checklist: [...(prev.practical_checklist || []), { id: `new-${Date.now()}`, text: '' }] }))} className="rounded-xl border-stone-200" data-testid="add-checklist-item-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Add Skill</Button>
          </div>
          <p className="text-xs text-stone-500 mb-3">Hands-on skills the manager must physically watch the staff member demonstrate before signing off this module.</p>
          <div className="space-y-2">
            {(module.practical_checklist || []).map((c, i) => (
              <div key={c.id || i} className="flex items-center gap-2">
                <span className="text-xs text-stone-400 w-5">{i + 1}.</span>
                <Input value={c.text} onChange={(e) => setModule(prev => { const list = [...(prev.practical_checklist || [])]; list[i] = { ...list[i], text: e.target.value }; return { ...prev, practical_checklist: list }; })} className="rounded-xl text-sm" placeholder="e.g. Demonstrate proper handwashing for 20 seconds" data-testid={`checklist-item-input-${i}`} />
                <Button variant="ghost" size="sm" onClick={() => setModule(prev => ({ ...prev, practical_checklist: (prev.practical_checklist || []).filter((_, j) => j !== i) }))} className="text-red-400 hover:text-red-600 h-8 w-8 p-0" data-testid={`remove-checklist-item-${i}`}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            {(module.practical_checklist || []).length === 0 && <p className="text-sm text-stone-400 text-center py-3 border border-dashed border-stone-200 rounded-xl">No practical skills — sign-off will only require the quiz</p>}
          </div>
        </CardContent>
      </Card>

      {/* Bottom Save Bar */}
      {lessons.length > 0 && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
          className="mt-6 flex items-center justify-between p-4 bg-white rounded-2xl border border-stone-200 shadow-sm">
          <p className="text-sm text-stone-500">{lessons.length} lesson{lessons.length !== 1 ? 's' : ''} in this module</p>
          <Button onClick={saveModule} disabled={saving} className="bg-[#1B2A4A] text-white rounded-xl hover:bg-[#243658]" data-testid="save-bottom-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save All Changes'}
          </Button>
        </motion.div>
      )}
    </div>
  );
}
